import { extensionFor, isImageType } from '../../lib/files'

/** Whether copied HTML carries words, not just a picture (a browser's Copy image puts an <img> there). */
function htmlHasText(html: string): boolean {
  if (!html) return false
  // A parsed document runs no scripts and loads nothing.
  return Boolean(new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim())
}

const twoDigits = (value: number) => String(value).padStart(2, '0')

/**
 * Pictures come off the clipboard without a name, or all as "image.png": each gets one from when it was pasted, such
 * as "Pasted image 14.32.05.png", so the ones sent can be told apart. Files with names of their own keep them, and so
 * does "image.heic", whose name says why it can't be sent. A nameless file of a type that can't be sent is still
 * "Pasted file 14.32.05", for the message saying so.
 */
function named(files: File[], now: Date): File[] {
  const time = `${twoDigits(now.getHours())}.${twoDigits(now.getMinutes())}.${twoDigits(now.getSeconds())}`
  let count = 0
  return files.map((file) => {
    const extension = extensionFor(file.type)
    if (file.name && (!/^image\.\w+$/i.test(file.name) || !extension)) return file
    count++
    const name = `Pasted ${isImageType(file.type) ? 'image' : 'file'} ${time}${count > 1 ? ` (${count})` : ''}`
    return new File([file], extension ? `${name}.${extension}` : name, { type: file.type })
  })
}

/**
 * Files pasted into the message box (spec §3.7), to attach as the file picker does: screenshots, copied photos and
 * files. Text pastes as usual, including text from Word, Excel and other documents, which put a picture of what was
 * copied on the clipboard as well.
 */
export function pastedFiles(clipboard: Pick<DataTransfer, 'files' | 'items' | 'types' | 'getData'>): File[] {
  let files = [...clipboard.files]
  if (files.length === 0) {
    // Some browsers list a pasted image only as an item.
    files = [...clipboard.items].flatMap((item) => (item.kind === 'file' ? (item.getAsFile() ?? []) : []))
  }
  if (files.length === 0) return []
  const text = clipboard.getData('text/plain').trim()
  if (text && ([...clipboard.types].includes('text/rtf') || htmlHasText(clipboard.getData('text/html')))) return []
  return named(files, new Date())
}
